import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Lock,
    Mail,
    Globe,
    AlertCircle,
    Check,
    Download,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/common/SEO";
import LiveCounter from "@/components/common/LiveCounter";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/tracker";
import { LIBRO } from "@/data/staticContent";

const COUNTRIES = [
    "Chile", "Argentina", "Colombia", "México", "Perú", "Bolivia", "Uruguay", "Paraguay",
    "Venezuela", "Ecuador", "España", "Estados Unidos", "Otro",
];

export default function Descargar() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: "",
        email: "",
        country: "Chile",
        accept: false,
    });
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        track("checkout_viewed", { sku: "EMI-2026-001", model: "free" });
    }, []);

    async function submit(e) {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.country) {
            toast.error("Completa nombre, email y país.");
            return;
        }
        if (!form.accept) {
            toast.error("Debes aceptar los términos para continuar.");
            return;
        }
        setLoading(true);
        track("checkout_started", {
            country: form.country,
            sku: "EMI-2026-001",
            amount: 0,
            is_free: true,
        });
        try {
            const { data } = await api.post("/checkout/create-preference", {
                name: form.name.trim(),
                email: form.email.trim(),
                country: form.country,
                sku: "EMI-2026-001",
                amount: 0,
            });

            if (data?.order_id) {
                track("download_completed", { is_free: true, order_id: data.order_id });
                navigate(`/gracias?order_id=${data.order_id}&status=approved`);
                return;
            }

            toast.info("No pudimos procesar tu descarga. Intenta de nuevo en unos segundos.");
        } catch (e) {
            toast.error(
                formatApiError(e.response?.data?.detail) ||
                    "No pudimos procesar tu solicitud. Intenta de nuevo.",
            );
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <SEO
                title="Descarga gratis el libro · El Principio del Mapa Inverso"
                description="Descarga 100% gratis el libro de Nelson González Rodríguez. Sin costo, sin condiciones, sin letra chica. Formato PDF + EPUB, entrega inmediata por email."
                path="/descargar"
                keywords="descargar libro adicción gratis, mapa inverso descarga gratis, libro nelson gonzález rodríguez pdf, libro recuperación adicción gratis Chile, cuentos para adictos, sinadicciones descarga"
            />

            <section className="py-16 md:py-20" data-testid="descargar-page">
                <div className="container-x max-w-5xl">
                    {/* Hero */}
                    <div className="mb-12 max-w-3xl">
                        <p className="eyebrow mb-4">Descarga gratuita</p>
                        <h1 className="font-display text-5xl md:text-7xl text-[var(--paper-2)] leading-[0.95] mb-6">
                            Descárgalo.{" "}
                            <span className="text-[var(--danger)]">
                                Sin costo, sin condiciones.
                            </span>
                        </h1>
                        <p className="font-mono text-base text-[var(--paper)] leading-relaxed mb-3">
                            El libro es <strong className="text-[var(--warning)]">100% gratis</strong>. No tienes que pagar, ni ahora ni después.
                        </p>
                        <p className="font-mono text-sm text-[var(--ink-muted)] leading-relaxed mb-6">
                            Si te sirve y quieres apoyar el trabajo territorial de{" "}
                            <a
                                href="https://sinadicciones.org"
                                target="_blank"
                                rel="noreferrer"
                                className="underline text-[var(--paper-2)] hover:text-[var(--warning)]"
                            >
                                SinAdicciones.org
                            </a>
                            , puedes hacerlo directo desde su sitio. Acá el foco es entregarte el libro sin fricción.
                        </p>
                        <LiveCounter variant="compact" />
                    </div>

                    <div className="grid lg:grid-cols-12 gap-10">
                        {/* Form */}
                        <form
                            onSubmit={submit}
                            className="lg:col-span-7 space-y-6"
                            data-testid="descargar-form"
                        >
                            {/* Datos personales */}
                            <div className="border-2 border-[var(--line)] bg-[#0d0d0d] p-6">
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--warning)] mb-4">
                                    01 · Tus datos
                                </p>
                                <p className="font-mono text-xs text-[var(--ink-muted)] mb-5">
                                    Solo necesitamos tu email para enviarte el link de descarga. Sin registro, sin contraseña, sin spam.
                                </p>
                                <div className="space-y-4">
                                    <Field
                                        label="Nombre"
                                        icon={Mail}
                                        value={form.name}
                                        onChange={(v) => setForm({ ...form, name: v })}
                                        placeholder="Tu nombre"
                                        testid="descargar-name"
                                        required
                                    />
                                    <Field
                                        label="Email"
                                        icon={Mail}
                                        type="email"
                                        value={form.email}
                                        onChange={(v) => setForm({ ...form, email: v })}
                                        placeholder="tu@email.com"
                                        testid="descargar-email"
                                        required
                                    />
                                    <div>
                                        <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] flex items-center gap-2 mb-2">
                                            <Globe className="w-3 h-3" />
                                            País
                                        </label>
                                        <select
                                            value={form.country}
                                            onChange={(e) => setForm({ ...form, country: e.target.value })}
                                            className="w-full bg-black border border-[var(--ink-dim)] px-3 py-2.5 font-mono text-sm text-[var(--paper-2)] focus:border-[var(--warning)] outline-none"
                                            data-testid="descargar-country"
                                        >
                                            {COUNTRIES.map((c) => (
                                                <option key={c} value={c}>
                                                    {c}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <label className="flex items-start gap-3 mt-5 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={form.accept}
                                        onChange={(e) => setForm({ ...form, accept: e.target.checked })}
                                        className="mt-1 cursor-pointer accent-[var(--danger)]"
                                        data-testid="descargar-accept"
                                    />
                                    <span className="font-mono text-xs text-[var(--ink-muted)] leading-relaxed">
                                        Acepto que el libro es de uso personal, sin redistribución
                                        comercial. No recibirás spam — solo tu libro y avisos
                                        importantes del proyecto.
                                    </span>
                                </label>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className={[
                                    "btn-brutal btn-brutal-danger w-full justify-center",
                                    loading ? "opacity-60 cursor-wait" : "",
                                ].join(" ")}
                                data-testid="descargar-submit"
                            >
                                {loading ? (
                                    <>
                                        <Sparkles className="w-4 h-4 animate-pulse" />
                                        Procesando…
                                    </>
                                ) : (
                                    <>
                                        <Download className="w-4 h-4" />
                                        Descargar gratis
                                    </>
                                )}
                            </button>
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] text-center inline-flex items-center gap-2 justify-center w-full">
                                <Lock className="w-3 h-3" />
                                Sin pago. Sin tarjeta. Solo tu email para enviarte el link.
                            </p>
                        </form>

                        {/* Aside */}
                        <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start space-y-6">
                            <div className="border-2 border-[var(--paper)] bg-[#0d0d0d] p-6">
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--warning)] mb-3">
                                    Resumen
                                </p>
                                <p className="font-display text-3xl text-[var(--paper-2)] mb-1">
                                    El Principio del Mapa Inverso
                                </p>
                                <p className="font-mono text-xs text-[var(--ink-muted)] mb-5">
                                    Nelson González Rodríguez · 141 págs · PDF + EPUB
                                </p>

                                <div className="border-t border-dashed border-[var(--ink-dim)] pt-4 space-y-2 mb-4">
                                    <Row k="Producto" v="Libro digital" />
                                    <Row k="Precio" v="Gratis" highlight />
                                </div>

                                <div className="border-t border-dashed border-[var(--ink-dim)] pt-4 space-y-3 font-mono text-xs text-[var(--paper)]">
                                    <p className="flex items-start gap-2">
                                        <Check className="w-4 h-4 text-[var(--warning)] flex-shrink-0 mt-0.5" />
                                        <span>Descarga inmediata por email</span>
                                    </p>
                                    <p className="flex items-start gap-2">
                                        <Check className="w-4 h-4 text-[var(--warning)] flex-shrink-0 mt-0.5" />
                                        <span>Sin DRM · re-descargas ilimitadas</span>
                                    </p>
                                    <p className="flex items-start gap-2">
                                        <Check className="w-4 h-4 text-[var(--warning)] flex-shrink-0 mt-0.5" />
                                        <span>PDF + EPUB · leelo en cualquier dispositivo</span>
                                    </p>
                                </div>
                            </div>

                            <div className="border border-dashed border-[var(--ink-dim)] p-5">
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-2 flex items-center gap-2">
                                    <AlertCircle className="w-3 h-3" />
                                    Importante
                                </p>
                                <p className="font-mono text-xs text-[var(--ink-muted)] leading-relaxed">
                                    Este libro no reemplaza terapia. Si estás en consumo activo o en
                                    riesgo, busca ayuda profesional o llama al{" "}
                                    <strong className="text-[var(--paper)]">600 360 7777</strong> (Chile · SENDA).
                                </p>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>
        </>
    );
}

function Field({ label, icon: Icon, type = "text", value, onChange, placeholder, testid, required }) {
    return (
        <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] flex items-center gap-2 mb-2">
                {Icon && <Icon className="w-3 h-3" />}
                {label}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                required={required}
                data-testid={testid}
                className="w-full bg-black border border-[var(--ink-dim)] px-3 py-2.5 font-mono text-sm text-[var(--paper-2)] focus:border-[var(--warning)] outline-none"
            />
        </div>
    );
}

function Row({ k, v, highlight }) {
    return (
        <div className="flex justify-between items-baseline gap-3">
            <dt className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--ink-muted)]">{k}</dt>
            <dd
                className={
                    highlight
                        ? "font-display text-xl text-[var(--warning)]"
                        : "font-mono text-xs text-[var(--paper-2)]"
                }
            >
                {v}
            </dd>
        </div>
    );
}
